/**
 * The Monitor page's three panes, read and wired (PRD-0075 R-B5, R-B6, ADR-0100): the inbox with
 * its filters, triage and accept dialog; the rules with their form; the standing sources.
 * `CaseMonitor` hands them to the presentational `MonitorWorkspace`.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type {
  AcceptAlertDialogProps,
  AcceptRequest,
  AcceptTarget,
} from '@/components/monitor/AcceptAlertDialog'
import type { AlertTriageActions } from '@/components/monitor/AlertTriage'
import { DEFAULT_INBOX_FILTERS, type InboxFilterValues } from '@/components/monitor/InboxFilters'
import type { RulesPaneProps } from '@/components/monitor/RulesPane'
import type { SourcesPaneProps } from '@/components/monitor/SourcesPane'
import type { MonitorInboxPane } from '@/components/program-view/workspaces/MonitorWorkspace'
import { documentPath } from '@/features/documents/document-path'
import { type AlertSort, MAX_INBOX_ALERTS, type TriageRequest } from '@/services/api/case-monitor'
import { inboxOf } from './inbox-groups'
import { investigationChoices, newestRaised, snoozeUntil, sourceRows } from './inbox-rows'
import { canChangeWatching, toLoadView } from './monitor-rows'
import { toRuleRows } from './rule-rows'
import { useMonitorAlerts, useWatchlists } from './useMonitor'
import {
  useAlertTriage,
  useInvestigationChoices,
  useMonitorVisit,
  useStandingSources,
} from './useMonitorInbox'
import { useRuleEditing } from './useWatchRules'

function toRequest(target: AcceptTarget): TriageRequest {
  return 'investigationId' in target
    ? { action: 'accept', investigation_id: target.investigationId }
    : { action: 'accept', new_investigation_title: target.newTitle }
}

/** Triage of an alert or a group from the inbox rows, and the accept dialog they open. */
function useTriageControls(caseId: string) {
  const triage = useAlertTriage(caseId)
  const [accepting, setAccepting] = useState<Omit<AcceptRequest, 'pending'> | null>(null)
  const choices = useInvestigationChoices(caseId, accepting !== null)
  const send = (alertIds: readonly string[], request: TriageRequest) =>
    triage.mutate({ alertIds, request })
  const actions: AlertTriageActions = {
    onAccept: (alertIds, label) => setAccepting({ alertIds, label }),
    onDismiss: alertIds => send(alertIds, { action: 'dismiss' }),
    onSnooze: (alertIds, length) =>
      send(alertIds, { action: 'snooze', until: snoozeUntil(new Date(), length) }),
    onReopen: alertIds => send(alertIds, { action: 'reopen' }),
    pending: triage.isPending,
  }
  const accept: AcceptAlertDialogProps = {
    request: accepting ? { ...accepting, pending: triage.isPending } : null,
    choices: toLoadView(choices, investigationChoices),
    onConfirm: (alertIds, target) =>
      triage.mutate(
        { alertIds, request: toRequest(target) },
        { onSuccess: () => setAccepting(null) }
      ),
    onClose: () => setAccepting(null),
  }
  return { actions, accept }
}

/**
 * The inbox: the case's newest alerts read once, kept to the filters on the page (new ones first
 * of all), counted for the chips and grouped one row per rule and source.
 */
export function useInboxPane(caseId: string): MonitorInboxPane {
  const navigate = useNavigate()
  const [sort, setSort] = useState<AlertSort>('date')
  const [filters, setFilters] = useState<InboxFilterValues>(DEFAULT_INBOX_FILTERS)
  const alerts = useMonitorAlerts(caseId)
  const watchlists = useWatchlists(caseId)
  const unreadSince = useMonitorVisit(caseId, newestRaised(alerts.data))
  const { actions, accept } = useTriageControls(caseId)
  const inbox = useMemo(
    () => inboxOf(alerts.data ?? [], unreadSince, filters, sort),
    [alerts.data, unreadSince, filters, sort]
  )
  return {
    view: toLoadView(alerts, () => inbox.groups),
    onOpenDocument: documentId => navigate(documentPath(caseId, documentId)),
    onRetry: () => void alerts.refetch(),
    sort: { value: sort, onChange: setSort },
    // With no alert at all the inbox says how alerts arrive; with some, that none matches.
    filtered: inbox.rows.length > 0,
    triage: {
      onOpenInvestigation: id => navigate(`/cases/${caseId}/investigations/${id}`),
      actions: canChangeWatching(watchlists.data?.member_role) ? actions : null,
    },
    filters: { value: filters, facets: inbox.facets, onChange: setFilters },
    accept,
    truncatedAt: inbox.rows.length >= MAX_INBOX_ALERTS ? MAX_INBOX_ALERTS : null,
  }
}

/** The rules, every one under its name, and the form for a reader who may change them. */
export function useRulesPane(caseId: string): RulesPaneProps {
  const watchlists = useWatchlists(caseId)
  const editing = useRuleEditing(caseId)
  return {
    view: toLoadView(watchlists, data => toRuleRows(data.watchlists)),
    edit: canChangeWatching(watchlists.data?.member_role) ? editing : null,
    onRetry: () => void watchlists.refetch(),
  }
}

export function useSourcesPane(caseId: string): SourcesPaneProps {
  const navigate = useNavigate()
  const sources = useStandingSources(caseId)
  return {
    view: toLoadView(sources, sourceRows),
    onOpenDocument: documentId => navigate(documentPath(caseId, documentId)),
    onRetry: () => void sources.refetch(),
  }
}
